import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PaymentsIcon from "@mui/icons-material/Payments";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import LendingOperationsApi from "../../../../infrastructure/api/lendingOperations";
import FinancialCoreApi from "../../../../infrastructure/api/financialCore";
import { toJalaliFullDate } from "../../../middleware/middleware";
import MehrsanLogAttachment from "./MehrsanLogAttachment";
import {
  LOAN_FOLLOW_UP_STATUSES,
  LOAN_PROCESS_TYPE_APPLICATION,
  LOAN_STATUS_APPROVED,
  LOAN_STATUS_PAID,
  getLoanStatusChipColor,
  resolveOperationsSource,
} from "./lendingOperationsConstants";
import { isCaseWorkspaceOperationsApi } from "./mehrsanOperationsConstants";
import ProcessOperationOwnerField from "./ProcessOperationOwnerField";
import {
  buildInitialLendingLogForm,
  buildStatusUpdateActionTitle,
  formatMoney,
  getAttachmentUrl,
  getAuthorName,
  getEntityId,
  getLoanStatusLabel,
  hasLoanApproval,
  hasLoanDisbursement,
  isLoanProcessClosed,
  normalizeTimelineLogs,
  parseApiErrorMessage,
  pickField,
} from "./lendingOperationsUtils";


const parseAmountForApi = (value) => {
  if (value === "" || value == null) return null;
  const n = Number(String(value).replace(/,/g, "").trim());
  return Number.isNaN(n) ? null : n;
};

const normalizeList = (res) => {
  const data = res?.data ?? res?.Data;
  return Array.isArray(data) ? data : [];
};

const ProcessCardSkeleton = () => (
  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
    <Skeleton variant="text" width="60%" height={22} />
    <Skeleton variant="text" width="40%" sx={{ mt: 0.5 }} />
    <Skeleton variant="rounded" width={72} height={28} sx={{ mt: 1 }} />
  </Paper>
);

const FinancialLines = ({ item, compact = false }) => {
  const requested = formatMoney(
    pickField(item, "requestedAmount", "RequestedAmount")
  );
  const approved = formatMoney(
    pickField(item, "approvedAmount", "ApprovedAmount")
  );
  const tracking = pickField(item, "trackingCode", "TrackingCode");
  const reference = pickField(item, "referenceNumber", "ReferenceNumber");

  const hasAny = requested || approved || tracking || reference;
  if (!hasAny) return null;

  return (
    <Stack spacing={0.25} sx={{ mt: compact ? 0.25 : 0.5 }}>
      {requested ? (
        <Typography variant="caption" color="text.secondary" component="div">
          مبلغ درخواستی: {requested}
        </Typography>
      ) : null}
      {approved ? (
        <Typography variant="caption" color="success.main" component="div">
          مبلغ تأییدشده: {approved}
        </Typography>
      ) : null}
      {tracking ? (
        <Typography variant="caption" color="text.secondary" component="div">
          کد رهگیری: {tracking}
        </Typography>
      ) : null}
      {reference ? (
        <Typography variant="caption" color="text.secondary" component="div">
          مرجع: {reference}
        </Typography>
      ) : null}
    </Stack>
  );
};

const LendingOperationsTab = ({
  requestInfoId,
  detailApi,
  fileBaseUrl = "",
  active = true,
  onStageChange,
}) => {
  const operationsSource = resolveOperationsSource(detailApi);
  const canEditAssignee = isCaseWorkspaceOperationsApi(detailApi);
  const lastLoadedRequestRef = useRef(null);

  const [view, setView] = useState("list");
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [processes, setProcesses] = useState([]);
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [submittingLog, setSubmittingLog] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [assigneeSaving, setAssigneeSaving] = useState(false);

  const [logForm, setLogForm] = useState(buildInitialLendingLogForm(null));
  const [disbursementDocNumber, setDisbursementDocNumber] = useState("");
  const [disbursementAmount, setDisbursementAmount] = useState("");

  const [createForm, setCreateForm] = useState({
    lendingPartnerId: "",
    requestedAmount: "",
    referenceNumber: "",
    trackingCode: "",
    description: "",
  });

  const loadPartners = useCallback(async () => {
    setPartnersLoading(true);
    try {
      const res = await LendingOperationsApi.getPartners(operationsSource);
      if (res?.isSuccess === false) {
        setPartners([]);
        return;
      }
      const list = normalizeList(res);
      setPartners(list);
      if (list.length) {
        const firstId = getEntityId(list[0]);
        setCreateForm((prev) =>
          prev.lendingPartnerId
            ? prev
            : { ...prev, lendingPartnerId: firstId ?? "" }
        );
      }
    } catch {
      setPartners([]);
    } finally {
      setPartnersLoading(false);
    }
  }, [operationsSource]);

  const loadProcesses = useCallback(
    async (force = false) => {
      if (!requestInfoId) return;
      const key = String(requestInfoId);
      if (!force && lastLoadedRequestRef.current === key) return;

      setLoading(true);
      setListError(null);
      try {
        const res = await LendingOperationsApi.getByRequestId(
          operationsSource,
          requestInfoId
        );
        if (res?.isSuccess === false) {
          setListError(
            parseApiErrorMessage(res, "بارگذاری فرآیندهای وام ناموفق بود.")
          );
          setProcesses([]);
          return;
        }
        setProcesses(normalizeList(res));
        lastLoadedRequestRef.current = key;
      } catch {
        setListError("بارگذاری فرآیندهای وام ناموفق بود.");
        setProcesses([]);
      } finally {
        setLoading(false);
      }
    },
    [operationsSource, requestInfoId]
  );

  const loadProcessDetail = useCallback(
    async (processId) => {
      if (!processId) return;
      setDetailLoading(true);
      try {
        const res = await LendingOperationsApi.getById(operationsSource, processId);
        const data = res?.data ?? res?.Data;
        if (res?.isSuccess === false || !data) {
          toast.error(
            parseApiErrorMessage(res, "بارگذاری جزئیات فرآیند ناموفق بود.")
          );
          return;
        }
        setSelectedProcess(data);
        setView("detail");
        setLogForm(buildInitialLendingLogForm(data));
        const approved =
          pickField(data, "approvedAmount", "ApprovedAmount") ??
          pickField(data, "requestedAmount", "RequestedAmount") ??
          "";
        setDisbursementAmount(approved !== "" && approved != null ? String(approved) : "");
        setDisbursementDocNumber(
          pickField(data, "trackingCode", "TrackingCode") ||
            pickField(data, "referenceNumber", "ReferenceNumber") ||
            ""
        );
      } catch {
        toast.error("بارگذاری جزئیات فرآیند ناموفق بود.");
      } finally {
        setDetailLoading(false);
      }
    },
    [operationsSource]
  );

  useEffect(() => {
    if (!active || !requestInfoId) return;
    const key = String(requestInfoId);
    if (lastLoadedRequestRef.current !== key) {
      setView("list");
      setSelectedProcess(null);
      setListError(null);
    }
    loadProcesses();
    loadPartners();
  }, [active, requestInfoId, loadProcesses, loadPartners]);

  useEffect(() => {
    if (createOpen && !partners.length) loadPartners();
  }, [createOpen, partners.length, loadPartners]);

  useEffect(() => {
    if (!active || !canEditAssignee) return;
    const loadExperts = async () => {
      try {
        const res = await LendingOperationsApi.getAssigneeOptions(operationsSource);
        if (res?.isSuccess) setAssigneeOptions(res.data || []);
      } catch {
        setAssigneeOptions([]);
      }
    };
    loadExperts();
  }, [active, operationsSource, canEditAssignee]);

  const saveProcessAssignee = useCallback(
    async (userId) => {
      const processId = getEntityId(selectedProcess);
      if (!processId) return;
      setAssigneeSaving(true);
      try {
        const res = await LendingOperationsApi.update(operationsSource, {
          id: processId,
          updateAssignedToUser: true,
          assignedToUserId: userId,
        });
        if (res?.isSuccess) {
          toast.success("مسئول فرآیند به‌روزرسانی شد");
          await loadProcessDetail(processId);
          await loadProcesses(true);
        } else {
          toast.error(
            res?.message || "کاربر انتخاب‌شده برای مسئول فرآیند معتبر نیست."
          );
        }
      } catch (err) {
        toast.error(
          parseApiErrorMessage(err, "کاربر انتخاب‌شده برای مسئول فرآیند معتبر نیست.")
        );
      } finally {
        setAssigneeSaving(false);
      }
    },
    [selectedProcess, operationsSource, loadProcessDetail, loadProcesses]
  );

  const timelineLogs = useMemo(
    () =>
      normalizeTimelineLogs(
        selectedProcess?.logs || selectedProcess?.Logs || []
      ),
    [selectedProcess]
  );

  const hasLoanProcess = processes.length > 0;
  const processClosed = isLoanProcessClosed(selectedProcess, timelineLogs);
  const loanApproved = hasLoanApproval(selectedProcess, timelineLogs);
  const loanDisbursed = hasLoanDisbursement(selectedProcess, timelineLogs);

  useEffect(() => {
    if (!active || loading || createOpen || view !== "list") return;
    if (processes.length === 1) {
      const id = getEntityId(processes[0]);
      if (id) loadProcessDetail(id);
    }
  }, [active, loading, createOpen, view, processes, loadProcessDetail]);

  const submitLog = async (payload, successMessage = "وضعیت وام بروزرسانی شد.") => {
    const processId = getEntityId(selectedProcess);
    if (!processId || submittingLog || processClosed) return false;

    setSubmittingLog(true);
    try {
      const res = await LendingOperationsApi.addLog(operationsSource, {
        LoanProcessId: processId,
        ...payload,
      });
      if (res?.isSuccess === false) {
        toast.error(parseApiErrorMessage(res, "ثبت وضعیت ناموفق بود."));
        return false;
      }
      toast.success(successMessage);
      setLogForm(buildInitialLendingLogForm(selectedProcess));
      await loadProcessDetail(processId);
      await loadProcesses(true);
      onStageChange?.();
      return true;
    } catch {
      toast.error("ثبت وضعیت ناموفق بود. اتصال یا دسترسی را بررسی کنید.");
      return false;
    } finally {
      setSubmittingLog(false);
    }
  };

  const handleStatusUpdate = async () => {
    const status = Number(logForm.status);
    await submitLog({
      Status: status,
      ActionTitle: buildStatusUpdateActionTitle(status),
      Description: logForm.description?.trim() || null,
    });
  };

  const handleQuickApproval = async () => {
    if (loanApproved) {
      toast.info("اتمام و تأیید وام قبلاً ثبت شده است.");
      return;
    }
    await submitLog(
      {
        Status: LOAN_STATUS_APPROVED,
        ActionTitle: "اتمام و تأیید وام",
        Description: logForm.description?.trim() || null,
        ApprovedAmount: parseAmountForApi(logForm.approvedAmount),
      },
      "اتمام و تأیید وام ثبت شد."
    );
  };

  const handleQuickDisbursement = async () => {
    if (!loanApproved) {
      toast.warning("ابتدا باید اتمام و تأیید وام ثبت شود.");
      return;
    }
    if (loanDisbursed) {
      toast.info("واریز وام قبلاً ثبت شده است.");
      return;
    }

    const processId = getEntityId(selectedProcess);
    if (!processId || submittingLog || processClosed) return;

    const tracking = (disbursementDocNumber || "").trim();
    const amount =
      parseAmountForApi(disbursementAmount) ??
      parseAmountForApi(logForm.approvedAmount) ??
      pickField(selectedProcess, "approvedAmount", "ApprovedAmount") ??
      pickField(selectedProcess, "requestedAmount", "RequestedAmount");

    if (!tracking) {
      toast.warning("شماره سند واریز را وارد کنید.");
      return;
    }
    if (!amount || Number(amount) <= 0) {
      toast.warning("مبلغ واریز وام را وارد کنید.");
      return;
    }

    const description =
      logForm.description?.trim() || `واریز وام — سند ${tracking}`;

    setSubmittingLog(true);
    try {
      const finRes = await FinancialCoreApi.registerLoanDisbursementTransaction(
        operationsSource,
        {
          requestInfoId: Number(requestInfoId),
          loanProcessId: processId,
          amount: Number(amount),
          trackingNumber: tracking,
          description,
        }
      );
      if (finRes?.isSuccess === false) {
        toast.error(
          parseApiErrorMessage(finRes, "ثبت تراکنش مالی واریز وام ناموفق بود.")
        );
        return;
      }

      const res = await LendingOperationsApi.addLog(operationsSource, {
        LoanProcessId: processId,
        Status: LOAN_STATUS_PAID,
        ActionTitle: "واریز وام",
        Description: description,
      });
      if (res?.isSuccess === false) {
        toast.warning(
          parseApiErrorMessage(
            res,
            "تراکنش مالی ثبت شد، اما بستن فرآیند وام ناموفق بود."
          )
        );
        await loadProcessDetail(processId);
        return;
      }

      toast.success("واریز وام (مالی و فرآیند) ثبت شد.");
      setLogForm(buildInitialLendingLogForm(selectedProcess));
      await loadProcessDetail(processId);
      await loadProcesses(true);
      onStageChange?.();
    } catch (err) {
      toast.error(parseApiErrorMessage(err, "ثبت واریز وام ناموفق بود."));
    } finally {
      setSubmittingLog(false);
    }
  };

  const handleCreateProcess = async () => {
    if (!requestInfoId || createLoading) return;
    if (!createForm.lendingPartnerId) {
      toast.warning("انتخاب لندتک الزامی است.");
      return;
    }
    if (hasLoanProcess) {
      toast.warning("فرآیند وام برای این پرونده قبلاً ثبت شده است.");
      return;
    }

    setCreateLoading(true);
    try {
      const res = await LendingOperationsApi.create(operationsSource, {
        RequestInfoId: Number(requestInfoId),
        LendingPartnerId: Number(createForm.lendingPartnerId),
        ProcessType: LOAN_PROCESS_TYPE_APPLICATION,
        RequestedAmount: parseAmountForApi(createForm.requestedAmount),
        ReferenceNumber: createForm.referenceNumber?.trim() || null,
        TrackingCode: createForm.trackingCode?.trim() || null,
        Description: createForm.description?.trim() || null,
      });
      if (res?.isSuccess === false) {
        toast.error(parseApiErrorMessage(res, "ثبت‌نام در لندتک ناموفق بود."));
        return;
      }
      toast.success("ثبت‌نام وام در لندتک انجام شد.");
      setCreateOpen(false);
      setCreateForm({
        lendingPartnerId: getEntityId(partners[0]) ?? "",
        requestedAmount: "",
        referenceNumber: "",
        trackingCode: "",
        description: "",
      });
      await loadProcesses(true);
      const createdId =
        res?.data?.id ??
        res?.data?.Id ??
        res?.Data?.id ??
        res?.Data?.Id;
      if (createdId) {
        await loadProcessDetail(createdId);
      }
    } catch {
      toast.error("ثبت‌نام در لندتک ناموفق بود.");
    } finally {
      setCreateLoading(false);
    }
  };

  const renderTimelineItem = (log) => {
    const logId = getEntityId(log);
    const status = pickField(log, "status", "Status");
    const statusLabel = getLoanStatusLabel(
      status,
      pickField(log, "statusTitle", "StatusTitle")
    );

    return (
      <Paper
        key={logId ?? `${log.actionTitle}-${log.createOn}`}
        variant="outlined"
        sx={{ p: 1.25, borderRadius: 1.5 }}
      >
        <Chip
          size="small"
          label={statusLabel}
          color={getLoanStatusChipColor(status)}
          variant="outlined"
          sx={{ mb: 0.75 }}
        />
        <Typography variant="subtitle2" fontWeight={600}>
          {log.actionTitle || log.ActionTitle || "—"}
        </Typography>
        <Stack spacing={0.35} sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" component="div">
            <AccessTimeIcon sx={{ fontSize: 13, verticalAlign: "middle", ml: 0.5 }} />
            {toJalaliFullDate(pickField(log, "createOn", "CreateOn")) || "—"}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            <AssignmentIndIcon sx={{ fontSize: 13, verticalAlign: "middle", ml: 0.5 }} />
            {getAuthorName(pickField(log, "createdByFullName", "CreatedByFullName"))}
          </Typography>
          {pickField(log, "nextFollowUpDate", "NextFollowUpDate") ? (
            <Typography variant="caption" color="warning.main" component="div">
              پیگیری بعدی:{" "}
              {toJalaliFullDate(pickField(log, "nextFollowUpDate", "NextFollowUpDate"))}
            </Typography>
          ) : null}
          <FinancialLines item={log} compact />
          {log.description || log.Description ? (
            <>
              <Divider sx={{ my: 0.5 }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.8rem", lineHeight: 1.5 }}
              >
                {log.description ?? log.Description}
              </Typography>
            </>
          ) : null}
          <MehrsanLogAttachment
            url={getAttachmentUrl(log)}
            fileBaseUrl={fileBaseUrl}
            compact
          />
        </Stack>
      </Paper>
    );
  };

  const renderProcessCard = (item) => {
    const itemId = getEntityId(item);
    const partnerTitle =
      pickField(item, "lendingPartnerTitle", "LendingPartnerTitle") || "—";
    const typeTitle = pickField(item, "processTypeTitle", "ProcessTypeTitle") || "—";
    const status = pickField(item, "currentStatus", "CurrentStatus");
    const statusTitle = getLoanStatusLabel(
      status,
      pickField(item, "currentStatusTitle", "CurrentStatusTitle")
    );

    return (
      <Paper key={itemId} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
        <Stack spacing={0.75}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, lineHeight: 1.35 }}>
              {pickField(item, "title", "Title") || typeTitle}
            </Typography>
            <Chip
              size="small"
              label={statusTitle}
              color={getLoanStatusChipColor(status)}
              variant="outlined"
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            لندتک: {partnerTitle}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            نوع: {typeTitle}
          </Typography>
          <FinancialLines item={item} compact />
          {pickField(item, "lastActionTitle", "LastActionTitle") ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
              آخرین اقدام: {pickField(item, "lastActionTitle", "LastActionTitle")}
            </Typography>
          ) : null}
          {pickField(item, "lastActionAt", "LastActionAt") ? (
            <Typography variant="caption" color="text.secondary">
              آخرین بروزرسانی:{" "}
              {toJalaliFullDate(pickField(item, "lastActionAt", "LastActionAt")) || "—"}
            </Typography>
          ) : null}
          {pickField(item, "assignedToFullName", "AssignedToFullName") ? (
            <Typography variant="caption" color="text.secondary">
              مسئول: {pickField(item, "assignedToFullName", "AssignedToFullName")}
            </Typography>
          ) : null}
          <Button
            size="small"
            variant="outlined"
            onClick={() => loadProcessDetail(itemId)}
            sx={{ alignSelf: "flex-start", mt: 0.25 }}
          >
            جزئیات
          </Button>
        </Stack>
      </Paper>
    );
  };

  if (loading && !processes.length && !listError) {
    return (
      <Stack spacing={1}>
        <ProcessCardSkeleton />
        <ProcessCardSkeleton />
      </Stack>
    );
  }

  if (listError && !processes.length) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => loadProcesses(true)}>
            تلاش مجدد
          </Button>
        }
      >
        {listError}
      </Alert>
    );
  }

  if (view === "detail") {
    const typeTitle = pickField(selectedProcess, "processTypeTitle", "ProcessTypeTitle");
    const partnerTitle = pickField(
      selectedProcess,
      "lendingPartnerTitle",
      "LendingPartnerTitle"
    );
    const statusTitle = getLoanStatusLabel(
      pickField(selectedProcess, "currentStatus", "CurrentStatus"),
      pickField(selectedProcess, "currentStatusTitle", "CurrentStatusTitle")
    );

    return (
      <Box sx={{ width: "100%" }}>
        {processes.length > 1 ? (
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              setView("list");
              setSelectedProcess(null);
            }}
            sx={{ mb: 1 }}
          >
            بازگشت به لیست
          </Button>
        ) : null}

        {detailLoading && !selectedProcess ? (
          <Stack spacing={1}>
            <Skeleton variant="rounded" height={88} />
            <Skeleton variant="rounded" height={100} />
          </Stack>
        ) : (
          <>
            <Paper variant="outlined" sx={{ p: 1.25, mb: 1.5, borderRadius: 1.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {pickField(selectedProcess, "title", "Title") || typeTitle}
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.75, gap: 0.5 }}>
                <Chip size="small" label={partnerTitle || "—"} variant="outlined" />
                <Chip size="small" label={typeTitle || "—"} variant="outlined" />
                <Chip
                  size="small"
                  label={statusTitle}
                  color={getLoanStatusChipColor(
                    pickField(selectedProcess, "currentStatus", "CurrentStatus")
                  )}
                />
              </Stack>
              <FinancialLines item={selectedProcess} />
            </Paper>

            {processClosed ? (
              <Alert severity="success" sx={{ mb: 1.5 }}>
                فرآیند وام بسته شده است.
                {loanApproved && loanDisbursed
                  ? " اتمام و واریز ثبت شده‌اند."
                  : null}
              </Alert>
            ) : null}

            {!processClosed ? (
              <Paper variant="outlined" sx={{ p: 1.25, mb: 1.5, borderRadius: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  مراحل پایانی وام
                </Typography>
                {loanApproved && !loanDisbursed ? (
                  <Alert severity="info" sx={{ mb: 1, py: 0.25 }}>
                    اتمام ثبت شده؛ مبلغ و شماره سند را وارد کنید و «ثبت واریز» را بزنید.
                  </Alert>
                ) : null}
                {loanApproved && !loanDisbursed ? (
                  <Stack spacing={1} sx={{ mb: 1 }}>
                    <TextField
                      label="مبلغ واریزی (ریال)"
                      size="small"
                      fullWidth
                      value={disbursementAmount}
                      onChange={(e) => setDisbursementAmount(e.target.value)}
                      disabled={submittingLog}
                    />
                    <TextField
                      label="شماره سند واریز"
                      size="small"
                      fullWidth
                      value={disbursementDocNumber}
                      onChange={(e) => setDisbursementDocNumber(e.target.value)}
                      disabled={submittingLog}
                    />
                  </Stack>
                ) : null}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<CheckCircleOutlineIcon />}
                    disabled={submittingLog || loanApproved}
                    onClick={handleQuickApproval}
                    fullWidth
                  >
                    {loanApproved ? "اتمام ثبت شده" : "ثبت اتمام"}
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PaymentsIcon />}
                    disabled={submittingLog || !loanApproved || loanDisbursed}
                    onClick={handleQuickDisbursement}
                    fullWidth
                  >
                    {loanDisbursed ? "واریز ثبت شده" : "ثبت واریز"}
                  </Button>
                </Stack>
                {!loanApproved ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                    پس از ثبت اتمام، دکمه واریز فعال می‌شود.
                  </Typography>
                ) : null}
              </Paper>
            ) : null}

            <Paper variant="outlined" sx={{ p: 1.25, mb: 1.5, borderRadius: 1.5 }}>
              <ProcessOperationOwnerField
                label="مسئول فرآیند"
                experts={assigneeOptions}
                assignedToUserId={pickField(
                  selectedProcess,
                  "assignedToUserId",
                  "AssignedToUserId"
                )}
                assignedToFullName={pickField(
                  selectedProcess,
                  "assignedToFullName",
                  "AssignedToFullName"
                )}
                assignedAt={pickField(selectedProcess, "assignedAt", "AssignedAt")}
                canEdit={canEditAssignee}
                saving={assigneeSaving}
                onAssigneeChange={saveProcessAssignee}
              />
            </Paper>

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 0.5 }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                تاریخچه وضعیت
              </Typography>
              {detailLoading ? <CircularProgress size={16} /> : null}
            </Stack>

            {!timelineLogs.length ? (
              <Alert severity="info" sx={{ mt: 0.5 }}>
                هنوز بروزرسانی وضعیتی ثبت نشده است.
              </Alert>
            ) : (
              <Stack spacing={1.25}>{timelineLogs.map(renderTimelineItem)}</Stack>
            )}

            {!processClosed ? (
              <Paper variant="outlined" sx={{ p: 1.25, mt: 1.5, borderRadius: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  بروزرسانی وضعیت
                </Typography>
                <Stack spacing={1.5}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>وضعیت</InputLabel>
                    <Select
                      label="وضعیت"
                      value={logForm.status}
                      disabled={submittingLog}
                      onChange={(e) =>
                        setLogForm((prev) => ({ ...prev, status: e.target.value }))
                      }
                    >
                      {LOAN_FOLLOW_UP_STATUSES.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="توضیحات (اختیاری)"
                    fullWidth
                    multiline
                    minRows={2}
                    disabled={submittingLog}
                    value={logForm.description}
                    onChange={(e) =>
                      setLogForm((prev) => ({ ...prev, description: e.target.value }))
                    }
                  />
                  {!loanApproved ? (
                    <TextField
                      size="small"
                      label="مبلغ تأییدشده (برای ثبت اتمام)"
                      fullWidth
                      disabled={submittingLog}
                      value={logForm.approvedAmount}
                      onChange={(e) =>
                        setLogForm((prev) => ({
                          ...prev,
                          approvedAmount: e.target.value,
                        }))
                      }
                    />
                  ) : null}
                  <Button
                    variant="outlined"
                    onClick={handleStatusUpdate}
                    disabled={submittingLog}
                  >
                    {submittingLog ? "در حال ثبت..." : "ثبت بروزرسانی وضعیت"}
                  </Button>
                </Stack>
              </Paper>
            ) : null}
          </>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          وام {hasLoanProcess ? "" : "(ثبت‌نام نشده)"}
        </Typography>
        <Stack direction="row" spacing={0.5} justifyContent={{ xs: "stretch", sm: "flex-end" }}>
          <Button
            size="small"
            variant="text"
            startIcon={<RefreshIcon />}
            onClick={() => loadProcesses(true)}
            disabled={loading}
          >
            بروزرسانی
          </Button>
          {!hasLoanProcess ? (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
            >
              ثبت‌نام در لندتک
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {listError ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {listError}
        </Alert>
      ) : null}

      {loading && processes.length > 0 ? (
        <Box display="flex" justifyContent="center" py={1}>
          <CircularProgress size={20} />
        </Box>
      ) : null}

      {!processes.length && !loading ? (
        <Alert severity="info">
          هنوز ثبت‌نام وام در لندتک انجام نشده است. برای شروع، لندتک و اطلاعات ثبت‌نام را وارد کنید.
        </Alert>
      ) : processes.length > 1 ? (
        <Stack spacing={1}>{processes.map(renderProcessCard)}</Stack>
      ) : null}

      <Dialog
        open={createOpen}
        onClose={() => !createLoading && setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>ثبت‌نام وام در لندتک</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Alert severity="info" sx={{ py: 0.5 }}>
              مرحله اول: انتخاب لندتک و ثبت اطلاعات ثبت‌نام. مراحل بعدی فقط بروزرسانی وضعیت است.
            </Alert>
            <FormControl size="small" fullWidth disabled={createLoading || partnersLoading}>
              <InputLabel>لندتک</InputLabel>
              <Select
                label="لندتک"
                value={createForm.lendingPartnerId}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    lendingPartnerId: e.target.value,
                  }))
                }
              >
                {partners.map((p) => (
                  <MenuItem key={getEntityId(p)} value={getEntityId(p)}>
                    {pickField(p, "title", "Title") ||
                      pickField(p, "code", "Code")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="مبلغ درخواستی"
              fullWidth
              disabled={createLoading}
              value={createForm.requestedAmount}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  requestedAmount: e.target.value,
                }))
              }
            />
            <TextField
              size="small"
              label="شماره پیگیری / مرجع"
              fullWidth
              disabled={createLoading}
              value={createForm.referenceNumber}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  referenceNumber: e.target.value,
                }))
              }
            />
            <TextField
              size="small"
              label="کد رهگیری"
              fullWidth
              disabled={createLoading}
              value={createForm.trackingCode}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  trackingCode: e.target.value,
                }))
              }
            />
            <TextField
              size="small"
              label="توضیحات"
              fullWidth
              multiline
              minRows={2}
              disabled={createLoading}
              value={createForm.description}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={createLoading}>
            انصراف
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateProcess}
            disabled={createLoading || !createForm.lendingPartnerId}
          >
            {createLoading ? "در حال ثبت..." : "ثبت‌نام"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

LendingOperationsTab.propTypes = {
  requestInfoId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  detailApi: PropTypes.string,
  fileBaseUrl: PropTypes.string,
  active: PropTypes.bool,
  onStageChange: PropTypes.func,
};

export default LendingOperationsTab;
